import {
  Address,
  AloftArea,
  AloftDate,
  AloftDateJSON,
  AloftPrice,
  MLSListing,
  PropertyData,
  RecommendedCompsSources,
  RecommendedCompsStatistics,
  SimilarityScoreBreakdown,
  SimilarityScoreCharacteristics,
  SimilarityScoreWeights,
  SixTierQuality,
  SubjectData,
  ToolkitListingID,
  ToolkitOrderID,
  schGeoSearch,
  schListingsFiltersMatchCriteria,
  schRecommendedCompsFilters,
} from '@aloft/toolkit-shared';
import { IconName, ProductColor } from '@aloft/toolkit-sui';
import { SortingState } from '@tanstack/react-table';

import { Dispatch, SetStateAction } from 'react';
import { z } from 'zod';

import { schRecommendCompsForm } from './schemas.ts';

export type CompSelectionProperty = {
  t: 'z' | 'abode' | 'tk';
  id: string;
  address?: Address;
  gla?: AloftArea;
  lotSize?: AloftArea;
  saleDate?: AloftDate;
  contractDate?: AloftDate;
  listingContractDate?: AloftDate;
  leaseDate?: AloftDate;
  rentRate?: AloftPrice;
  salePrice?: AloftPrice;
  listPrice?: AloftPrice;
  distanceToSubject?: number;
  imageUrls?: string[];
  latitude?: number;
  longitude?: number;
  fullBaths?: number;
  halfBaths?: number;
  bedroomCount?: number;
  yearBuilt?: number;
  bedrooms?: number;
  quality?: number;
  condition?: number;
  listingID?: ToolkitListingID;
  similarityScore?: number;
  similarityRank?: number;
  similarityBreakdown?: SimilarityScoreBreakdown;
  similarityCharacteristics?: SimilarityScoreCharacteristics;
  cumulativeDaysOnMarket?: number;
  basement?: boolean;
  garage?: boolean;
  reo?: boolean;
  shortSale?: boolean;
  hasMultipleListings?: boolean;
  mlsStatus?: string;
  garageCount?: number;
  pool?: boolean;
  propertyType?: string;
  qualityTier?: number;
  uadQualityRating?: SixTierQuality;
  uadConditionRating?: SixTierQuality;
  uadQualityRatingCommentary?: string;
  uadConditionRatingCommentary?: string;
  uadRatingsLoading?: boolean;
  uadRatingsSkipped?: boolean;
  uadQualityRatingFailed?: boolean;
  uadConditionRatingFailed?: boolean;
  listingData?: MLSListing;
};

export type CompTypeSelection = {
  aiv?: boolean;
  arv?: boolean;
  listingData?: MLSListing;
  uadQualityRating?: SixTierQuality;
  uadConditionRating?: SixTierQuality;
};

export type CompSelectionState = Record<string, CompTypeSelection>;

type RowSelectionState = {
  rowSelection: CompSelectionState;
  setRowSelection: Dispatch<SetStateAction<CompSelectionState>>;
};

type TableSortingState = {
  sorting: SortingState;
  setSorting: Dispatch<SetStateAction<SortingState>>;
};

export type RecommendedCompSelectionGridProps = RowSelectionState &
  TableSortingState & {
    setFocusedRow: Dispatch<SetStateAction<string | undefined>>;
    hoveredPropertyId?: string;
    setHoveredPropertyId: Dispatch<SetStateAction<string | undefined>>;
    data: CompSelectionProperty[];
    subject: CompSelectionProperty;
    comps: CompSelectionProperty[];
    focusedPropertyIndex?: number;
    source: RecommendedCompsSources;
    orderID: string;
    isInspectifyOrder: boolean;
    hasArvOption: boolean;
    compSelectionTableVariant?: 'sales' | 'rental';
    isExpanded?: boolean;
    onFilterOpen?: () => void;
    onColumnOrderChange?: (columnOrder: string[]) => void;
    columnOrder?: string[];
    columnVisibility?: Record<string, boolean>;
    isFilterPanelOpen?: boolean;
    onPinnedExtraCountChange?: (count: number) => void;
  };

export type RecommendedCompSelectionProps = RowSelectionState &
  TableSortingState &
  RecommendedCompSelectionBaseProps;

type RecommendedCompSelectionBaseProps = {
  compType?: 'sales' | 'rental';
  orderID: ToolkitOrderID;
  subject: SubjectData;
  comps: Record<string, PropertyData>;
  compSeq: string[];
  effectiveDate: AloftDateJSON;
  showNoRecommendations: boolean;
  allowRecommendedCompsFiltering: boolean;
  isInspectifyOrder: boolean;
  hasArvOption: boolean;
  onClose: () => void;
  setShowNoRecommendations: Dispatch<SetStateAction<boolean>>;
  getDefaultFormValues: () => InitialCompSearch['body']['search'];
  allowNewSimilarityScore?: boolean;
  similarityScoreWeights?: SimilarityScoreWeights;
  updateCompsLabel?: string;
  onUpdateComps?: () => void;
  onDeselectAll?: () => void;
  onFilterOpen?: () => void;
  onColumnOrderChange?: (columnOrder: string[]) => void;
  columnOrder?: string[];
  columnVisibility?: Record<string, boolean>;
  isFilterPanelOpen?: boolean;
  proximityRequirementMiles?: number;
  onQualityTierBreakdownDataChange?: (data: QualityTierBreakdownData) => void;
  onStatisticsChange?: (statistics: RecommendedCompsStatistics) => void;
};

export type CompSelectionProps = RowSelectionState &
  TableSortingState & {
    data: CompSelectionProperty[];
    subject: CompSelectionProperty;
    comps: CompSelectionProperty[];
    source: RecommendedCompsSources;
    orderID: string;
    isInspectifyOrder: boolean;
    hasArvOption: boolean;
    effectiveDate: AloftDate;
    compSelectionTableVariant?: 'sales' | 'rental';
    isExpanded?: boolean;
    totalComps?: number;
    onExpand?: () => void;
    onCollapse?: () => void;
    updateCompsLabel?: string;
    onUpdateComps?: () => void;
    onDeselectAll?: () => void;
    onFilterOpen?: () => void;
    onColumnOrderChange?: (columnOrder: string[]) => void;
    columnOrder?: string[];
    columnVisibility?: Record<string, boolean>;
    isFilterPanelOpen?: boolean;
    proximityRequirementMiles?: number;
  };

// ///////////////////////
// Overall Zod Schema
// ///////////////////////
export type RecommendCompsFormSchema = typeof schRecommendCompsForm;
export type RecommendCompsFormOutput = z.infer<typeof schRecommendCompsForm>;

export type InitialCompSearch = z.infer<typeof schRecommendedCompsStudy>;
export const schRecommendedCompsStudy = z.object({
  body: z.object({
    search: z.object({
      filters: schRecommendedCompsFilters,
      geoSearch: schGeoSearch,
      matchCriteria: schListingsFiltersMatchCriteria,
    }),
    originalSearch: z.object({
      filters: schRecommendedCompsFilters,
      geoSearch: schGeoSearch,
      matchCriteria: schListingsFiltersMatchCriteria,
    }),
  }),
});

export const schCompSearch = z.object({
  filters: schRecommendedCompsFilters,
  geoSearch: schGeoSearch,
  matchCriteria: schListingsFiltersMatchCriteria,
});
export type CompSearch = z.infer<typeof schCompSearch>;

export type FiltersMessage = {
  type: 'error' | 'warning' | 'success';
  color: ProductColor;
  message: string;
  icon: IconName;
};

export type QualityTierBreakdownData = Record<
  number,
  {
    properties: CompSelectionProperty[];
    priceBand?: string;
    medianGla?: string;
    countOfSales?: number;
  }
>;
